import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useIsFetching } from '@tanstack/react-query'
import KNDataTable from 'components/KNDataTable/KNDataTable'
import { Role } from 'context/authentication/Role.types'
import { useVehiclesView } from 'context/VehiclesViewContext'
import { analyticsEvent } from 'global/helpers/analytics'
import { hasAccess } from 'global/helpers/authorization'
import { replaceHighlights } from 'global/helpers/highlights'
import useDialog from 'global/hooks/useDialog'
import { type ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CreateOrEditVehicleDialog from './CreateOrEditVehicleDialog'
import DeleteVehicleDialog from './DeleteVehicleDialog'
import TrackingLabel from './TrackingLabel'
import { VEHICLES_QUERY_KEY } from './VehicleManager.service'
import type { VehicleData } from './VehicleManager.types'

interface VehicleTableProps {
  readonly vehicles: VehicleData[]
}

const VehicleTable = ({ vehicles }: VehicleTableProps): ReactElement => {
  const { t } = useTranslation()
  const [activeVehicle, setActiveVehicle] = useState<VehicleData>()
  const { filters } = useVehiclesView()
  const createOrEditVehicleDialog = useDialog<typeof CreateOrEditVehicleDialog>()
  const deleteVehicleDialog = useDialog<typeof DeleteVehicleDialog>()
  const isFetching = useIsFetching({ queryKey: [VEHICLES_QUERY_KEY] })

  const handleEditClick = useCallback((row: VehicleData) => {
    setActiveVehicle(row)
    createOrEditVehicleDialog.handleOpen()
    analyticsEvent('polestar_cs_edit_vehicle_button_clicked')
  }, [])

  const handleDeleteClick = useCallback((row: VehicleData) => {
    setActiveVehicle(row)
    deleteVehicleDialog.handleOpen()
    analyticsEvent('polestar_cs_delete_vehicle_button_clicked')
  }, [])

  const licensePlateColumnValue = (row: VehicleData) => (
    <Stack spacing={1} direction="row" alignItems="center">
      <Box>{replaceHighlights(row.licensePlate, filters.keywords)}</Box>
      {row.status && <TrackingLabel vehicle={row} />}
    </Stack>
  )

  const vehicleTypeColumnValue = (row: VehicleData) =>
    t(`screens.cs.vehicle_manager.vehicle_types.${row.vehicleType}`)

  const weightCapacityColumnValue = (row: VehicleData) => {
    if (row.weightCapacity) {
      return (
        <>
          {row.weightCapacity} <small>kg</small>
        </>
      )
    }
    return (
      <Typography variant="p3" color="primary.light">—</Typography>
    )
  }

  const fuelTypeColumn = (row: VehicleData) => {
    if (row.fuelType) {
      return t(`screens.cs.vehicle_manager.fuel_types.${row.fuelType}`)
    }
    return (
      <Typography variant="p3" color="primary.light">—</Typography>
    )
  }

  const makeModelColumn = (row: VehicleData) => {
    if (row.makeModel) {
      return row.makeModel
    }
    return (
      <Typography variant="p3" color="primary.light">—</Typography>
    )
  }

  const columns = useMemo(
    () => [
      {
        name: 'displayLicensePlate',
        label: t('screens.cs.vehicle_manager.card.columns.license_plate'),
        getValue: licensePlateColumnValue,
        sx: { width: { xs: '24rem', xxl: 'auto' } },
      },
      {
        name: 'vehicleType',
        label: t('screens.cs.vehicle_manager.card.columns.vehicle_type'),
        getValue: vehicleTypeColumnValue,
        sx: { width: '12rem' },
      },
      {
        name: 'weightCapacity',
        label: t('screens.cs.vehicle_manager.card.columns.weight_capacity'),
        getValue: weightCapacityColumnValue,
        sx: { width: '12rem' },
      },
      {
        name: 'fuelType',
        label: t('screens.cs.vehicle_manager.card.columns.fuel_type'),
        getValue: fuelTypeColumn,
        sx: { width: '12rem' },
      },
      {
        name: 'makeModel',
        label: t('screens.cs.vehicle_manager.card.columns.make_model'),
        getValue: makeModelColumn,
        sx: { width: '12rem' },
      },
    ],
    [filters.keywords],
  )

  const actions = useMemo(
    () => [
      {
        name: 'edit',
        label: t('screens.cs.vehicle_manager.card.actions.edit_short'),
        icon: <EditIcon />,
        onClick: handleEditClick,
        isVisible: (row: VehicleData): boolean => !row.telematicsReady,
      },
      {
        name: 'delete',
        label: t('screens.cs.vehicle_manager.card.actions.delete'),
        icon: <DeleteIcon />,
        onClick: handleDeleteClick,
        isVisible: (row: VehicleData): boolean => !row.telematicsReady,
      },
    ],
    [],
  )

  return (
    <>
      <KNDataTable
        columns={columns}
        actions={hasAccess(Role.Editor) ? actions : []}
        data={vehicles}
        isLoading={isFetching > 0}
        sx={{
          marginX: -2,
        }}
      />
      {activeVehicle && hasAccess(Role.Editor) && (
        <>
          <CreateOrEditVehicleDialog
            payload={{
              vehicle: activeVehicle,
            }}
            open={createOrEditVehicleDialog.isOpen}
            onAction={createOrEditVehicleDialog.handleAction}
            onClose={createOrEditVehicleDialog.handleClose}
          />
          <DeleteVehicleDialog
            payload={{
              vehicle: activeVehicle,
            }}
            open={deleteVehicleDialog.isOpen}
            onAction={deleteVehicleDialog.handleAction}
            onClose={deleteVehicleDialog.handleClose}
          />
        </>
      )}
    </>
  )
}

export default VehicleTable
